/**
 * useMissionRunHistory — every earlier run of one mission, newest first,
 * loaded in pages from `GET /v1/executions?mission_id=` (agion/Agion#2874).
 *
 * The sidebar's RECENT section draws its first five runs per mission from
 * the organisation's newest 50 runs (useConversationHistory). This hook backs
 * its "show all" list: it reads nothing until a person asks for the list,
 * then pages through the mission's runs as the server orders them
 * (newest first). It lists the same statuses as the RECENT section. Both
 * sidebars call it; each draws the list in its own design.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { executionsApi, type ExecutionListScope, type ExecutionResponse } from '../services/api'
import type { ConversationHistoryEntry } from '../types/chat'
import { isListedRunStatus, toConversationHistoryEntry } from './useConversationHistory'
import { useMissionStore } from './useMissions'

/** Runs requested per page; the server caps a page at 100. */
export const MISSION_RUN_PAGE_SIZE = 20

interface MissionRunHistoryState {
  rows: ExecutionResponse[]
  /** Offset of the next page, counted in server rows (all statuses). */
  nextOffset: number
  /** The server's count of the mission's runs; null before the first page. */
  total: number | null
  isLoading: boolean
  failure: unknown
}

const INITIAL: MissionRunHistoryState = { rows: [], nextOffset: 0, total: null, isLoading: false, failure: null }

export interface UseMissionRunHistoryReturn {
  /** The mission's listed runs loaded so far, newest first. */
  entries: ConversationHistoryEntry[]
  /** True once the first page has arrived. */
  loaded: boolean
  /** The server holds runs beyond the pages loaded so far. */
  hasMore: boolean
  isLoading: boolean
  /** The failed read itself, for screens that word it from its typed fields (ADR-20260925). */
  failure: unknown
  /** Reads the next page. */
  loadMore: () => void
  /** Repeats the read that failed. */
  retry: () => void
}

export function useMissionRunHistory(
  missionId: string,
  enabled: boolean,
  scope?: ExecutionListScope,
): UseMissionRunHistoryReturn {
  const [state, setState] = useState<MissionRunHistoryState>(INITIAL)
  const getMissionById = useMissionStore((s) => s.getMissionById)
  // Subscribe to missions array so entries recompute when missions load
  const missions = useMissionStore((s) => s.missions)
  // Each read carries the generation it started in; a reply from an earlier
  // mission or an abandoned list is dropped.
  const generation = useRef(0)

  const readPage = useCallback(
    async (offset: number) => {
      const started = generation.current
      setState((s) => ({ ...s, isLoading: true, failure: null }))
      try {
        const response = scope
          ? await executionsApi.listMissionExecutions(missionId, MISSION_RUN_PAGE_SIZE, offset, scope)
          : await executionsApi.listMissionExecutions(missionId, MISSION_RUN_PAGE_SIZE, offset)
        if (started !== generation.current) return
        const page = response.executions ?? []
        setState((s) => {
          // A run started since the previous page shifts the offsets by one;
          // a run already listed is not listed twice.
          const seen = new Set(s.rows.map((r) => r.id))
          return {
            rows: offset === 0 ? page : [...s.rows, ...page.filter((r) => !seen.has(r.id))],
            nextOffset: offset + page.length,
            total: page.length === 0 ? offset : response.total,
            isLoading: false,
            failure: null,
          }
        })
      } catch (err) {
        if (started !== generation.current) return
        setState((s) => ({ ...s, isLoading: false, failure: err }))
      }
    },
    [missionId, scope],
  )

  // Asked for: read the first page. Closed or another mission: forget.
  useEffect(() => {
    generation.current += 1
    if (!enabled) {
      setState(INITIAL)
      return
    }
    void readPage(0)
  }, [enabled, readPage])

  const { isLoading, nextOffset, total } = state
  const loadMore = useCallback(() => {
    if (!enabled || isLoading) return
    void readPage(nextOffset)
  }, [enabled, isLoading, nextOffset, readPage])

  // A failed first page is read again from the start; a failed later page
  // from where the list stopped.
  const retry = useCallback(() => {
    if (!enabled || isLoading) return
    void readPage(total === null ? 0 : nextOffset)
  }, [enabled, isLoading, nextOffset, total, readPage])

  const entries = useMemo(
    () =>
      state.rows
        .filter((execution) => isListedRunStatus(execution.status))
        .map((execution) => toConversationHistoryEntry(execution, getMissionById(execution.mission_id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- missions triggers recompute when store loads
    [state.rows, getMissionById, missions],
  )

  return {
    entries,
    loaded: total !== null,
    hasMore: total !== null && nextOffset < total,
    isLoading,
    failure: state.failure,
    loadMore,
    retry,
  }
}
